import { useEffect, useRef, useState } from "react";

import { preloadAvatars } from "~/lib/avatar-media";
import type { AuthenticatedMediaThumbnail } from "~/hooks/useAuthenticatedMedia";
import type { Client } from "~/lib/matrix";

const AVATAR_REVEAL_CAP_MS = 400;

export function useAvatarReveal(
  client: Client,
  roomId: string,
  hasRows: boolean,
  thumb: AuthenticatedMediaThumbnail,
  mxcs: string[],
): boolean {
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const settled = settledKey === roomId;
  const scopeRef = useRef<{
    client: Client | null;
    roomId: string;
    thumb: AuthenticatedMediaThumbnail;
    seen: Set<string>;
  }>({ client: null, roomId, thumb, seen: new Set() });
  const mxcsKey = mxcs.join("\n");

  useEffect(() => {
    if (!client || !hasRows) return;
    let cancelled = false;
    const scope = scopeRef.current;
    const seen =
      scope.client === client && scope.roomId === roomId && scope.thumb === thumb
        ? scope.seen
        : new Set<string>();
    scopeRef.current = { client, roomId, thumb, seen };
    if (settled) {
      const fresh = mxcs.filter((mxc) => !seen.has(mxc));
      for (const mxc of fresh) seen.add(mxc);
      if (fresh.length > 0) void preloadAvatars(client, fresh, thumb);
      return;
    }
    for (const mxc of mxcs) seen.add(mxc);
    if (mxcs.length === 0) {
      setSettledKey(roomId);
      return;
    }
    void preloadAvatars(client, mxcs, thumb).then(() => {
      if (!cancelled) setSettledKey(roomId);
    });
    return () => {
      cancelled = true;
    };
  }, [client, roomId, hasRows, settled, mxcsKey, thumb]);

  useEffect(() => {
    if (!client || !hasRows || settled) return;
    const cap = setTimeout(() => setSettledKey(roomId), AVATAR_REVEAL_CAP_MS);
    return () => clearTimeout(cap);
  }, [client, roomId, hasRows, settled]);

  return settled;
}
